import type { AxiosRequestConfig } from "axios";

/**
 * Reads that fail because the backend is busy (a throttled Lambda, a gateway timeout) or the
 * network blinked are retried a few times with backoff, so a short spike shows up as a delay
 * instead of an error. Writes are never retried: repeating one could do it twice.
 */
export const RETRY_DELAYS_MS = [500, 1000, 2000];

const RETRY_STATUSES = new Set([429, 502, 503, 504]);

// Reads the API takes as POST because of their filters
const POST_READS = [/\/pipelines\/combined(?:[/?]|$)/];

/** Marks an error the client already retried, so react-query doesn't retry it again */
export const RETRIED = "retriedByClient";

export function isRead(config: Pick<AxiosRequestConfig, "method" | "url">) {
    const method = (config.method || "get").toLowerCase();
    if (method === "get") return true;
    return method === "post" && POST_READS.some(pattern => pattern.test(config.url || ""));
}

export function isTransient(error: any) {
    if (error?.code === "ERR_CANCELED") return false;
    const status = error?.response?.status;
    return status === undefined ? !!error?.isAxiosError : RETRY_STATUSES.has(status);
}

export function shouldRetry(config: Pick<AxiosRequestConfig, "method" | "url">, error: unknown, attempt: number) {
    return attempt < RETRY_DELAYS_MS.length && isRead(config) && isTransient(error);
}

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/** Sends a request, retrying transient read failures; the wait between tries holds no slot */
export async function withRetry<T>(
    config: Pick<AxiosRequestConfig, "method" | "url">,
    send: () => Promise<T>,
    sleep: (ms: number) => Promise<unknown> = wait,
): Promise<T> {
    for (let attempt = 0; ; attempt++) {
        try {
            return await send();
        } catch (error: any) {
            if (!shouldRetry(config, error, attempt)) {
                if (attempt > 0 && error && typeof error === "object") error[RETRIED] = true;
                throw error;
            }
            await sleep(RETRY_DELAYS_MS[attempt] + Math.random() * 250);
        }
    }
}
